import { ChangeEvent, FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { Box, Card, ElementContainer, MultiSelectChip, SideDrawer, TextField, withStyles } from 'vopay';
import type { AccountUserRole } from 'vopay';
import { Button } from 'UI';
import { useDataFetchIndicator } from '@/hooks';
import { useToast } from '@/providers/Toast.provider';
import usePostCreateAccountUser from '@/hooks/useApi/usePostCreateAccountUser';
import useAssignUserRole from '@/hooks/useApi/useAssignUserRole';
import styles from './CreateUserDrawer.module.scss';

const cn = withStyles(styles);

type CreateUserDrawerProps = {
  isOpen: boolean;
  existingUsernames: string[];
  availableRoles?: AccountUserRole[];
  onClose: () => void;
  onUserCreated?: () => void;
  /**
   * If provided, the role dropdown shows a "+ New Role" footer button. The
   * callback receives the form's current in-progress state so the parent
   * can preserve it across the Create-Role chain. Pass only when the admin
   * has `ManageUserRoles`.
   */
  onCreateNewRole?: (currentState: { emailAddress: string; selectedRoleIds: number[] }) => void;
  /**
   * Initial values applied on each open (used to restore state when the
   * drawer is reopened after the Create Role chain).
   */
  initialEmailAddress?: string;
  initialRoleIds?: number[];
};

type RoleOption = { value: string; label: string };

const CreateUserDrawer: FC<CreateUserDrawerProps> = ({
  isOpen,
  existingUsernames,
  availableRoles = [],
  onClose,
  onUserCreated,
  onCreateNewRole,
  initialEmailAddress,
  initialRoleIds,
}) => {
  const { t } = useTranslation('translations', { keyPrefix: 'Users.UsersList.CreateUser' });
  const [, , createUserAsync] = usePostCreateAccountUser({});
  const { assignUserRole } = useAssignUserRole();
  const [, setLoading] = useDataFetchIndicator();
  const toast = useToast();
  const [emailAddress, setEmailAddress] = useState('');
  const [selectedRoleIds, setSelectedRoleIds] = useState<number[]>([]);

  // Track whether the drawer was previously open — we only hydrate from
  // initial props on a true closed→open transition to avoid resetting state
  // mid-edit when props change while the drawer is still open.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpen.current) {
      setEmailAddress(initialEmailAddress ?? '');
      setSelectedRoleIds(initialRoleIds ?? []);
    }
    wasOpen.current = isOpen;
  }, [isOpen, initialEmailAddress, initialRoleIds]);

  const { isEmailValid, isDuplicateEmail, emailInlineError } = useMemo(() => {
    const valid = Yup.string().email().required().isValidSync(emailAddress);
    const duplicate = existingUsernames.some(
      (existing) => existing.toLowerCase() === emailAddress.toLowerCase(),
    );
    const inlineError = duplicate
      ? t('duplicateEmail', {
          defaultValue: 'This email is already used. Please use a different email.',
        })
      : undefined;
    return { isEmailValid: valid, isDuplicateEmail: duplicate, emailInlineError: inlineError };
  }, [emailAddress, existingUsernames, t]);

  const roleOptions: RoleOption[] = availableRoles.map((role) => ({
    value: String(role.RoleID),
    label: role.Name,
  }));
  const selectedRoleValues = selectedRoleIds.map(String);

  function handleRolesChange(
    _event: ChangeEvent<HTMLInputElement>,
    args: { options: RoleOption[] },
  ) {
    setSelectedRoleIds(args.options.map((opt) => Number(opt.value)));
  }

  function handleRoleDelete(value: string) {
    setSelectedRoleIds((prev) => prev.filter((id) => String(id) !== value));
  }

  async function handleCreate() {
    try {
      setLoading(true, 'creating user', 'fullScreen');
      const res = await createUserAsync({ EmailAddress: emailAddress, SendEmail: true });
      if (!res.Success) {
        throw new Error(res.ErrorMessage);
      }

      // User created. If roles were selected, fire assigns in parallel; tally
      // partial failures so the toast can be honest about what happened.
      if (selectedRoleIds.length > 0) {
        const assignResults = await Promise.allSettled(
          selectedRoleIds.map((RoleID) =>
            assignUserRole({ Username: emailAddress, RoleID }),
          ),
        );
        const succeeded = assignResults.filter((r) => r.status === 'fulfilled').length;
        const failed = assignResults.length - succeeded;

        if (failed === 0) {
          toast.success(
            t('successMessageWithRoles', {
              defaultValue: `User was successfully created and ${succeeded} role(s) assigned.`,
            }),
          );
        } else if (succeeded > 0) {
          toast.neutral(
            t('partialRolesAssigned', {
              defaultValue: `User created. ${succeeded} of ${assignResults.length} roles assigned (${failed} failed).`,
            }),
          );
        } else {
          toast.neutral(
            t('rolesAssignFailed', {
              defaultValue: 'User was created, but the role assignments failed.',
            }),
          );
        }
      } else {
        toast.success(t('successMessage', { defaultValue: 'User was successfully created.' }));
      }

      onUserCreated?.();
      onClose();
    } catch (err) {
      console.error('Create user failed:', err);
      const message = err instanceof Error ? err.message : 'Unknown error';
      toast.neutral(
        t('failMessage', { defaultValue: `Unable to create the user. ${message}` }),
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <SideDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={t('title', { defaultValue: 'Create User' })}
      footer={
        <Box className={cn('footer')}>
          <Button
            colorScheme="primary"
            disabled={!isEmailValid || isDuplicateEmail}
            onClick={handleCreate}
          >
            {t('createButton', { defaultValue: 'Create User' })}
          </Button>
        </Box>
      }
    >
      <div className={cn('body')}>
        <Card hoverEffect={false}>
          <Box flx="col" gapSize="16px">
            <ElementContainer
              label={t('emailAddress', { defaultValue: 'Email Address' })}
              required
              isBold={false}
              padding="0 0 6px"
              htmlFor="newUserEmail"
              input={
                <TextField
                  name="newUserEmail"
                  placeholder={t('emailPlaceholder', { defaultValue: 'alex.rogers@acme.com' })}
                  value={emailAddress}
                  onChange={(e) => setEmailAddress(e.target.value)}
                  variant="outlineShadow"
                  error={emailInlineError}
                  errorFitContent
                />
              }
            />

            {availableRoles.length > 0 && (
              <ElementContainer
                label={t('selectRoles', { defaultValue: 'Select Role(s)' })}
                isBold={false}
                padding="0 0 6px"
                htmlFor="newUserRoles"
                input={
                  <MultiSelectChip
                    name="newUserRoles"
                    options={roleOptions}
                    value={selectedRoleValues}
                    onChange={handleRolesChange}
                    onDelete={handleRoleDelete}
                    placeholder={t('selectRolesPlaceholder', { defaultValue: 'Select' })}
                    filterable
                    emptyFilterMsg={t('noRolesFound', { defaultValue: 'No roles found' })}
                    renderOption={(o, { Option, SelectedIcon, selected }) => (
                      <Option>
                        {o.label}
                        {selected && <SelectedIcon />}
                      </Option>
                    )}
                    renderDropdownFooter={
                      onCreateNewRole
                        ? ({ close }) => (
                            <Button
                              iconLeft="add"
                              style={{ width: '100%' }}
                              onClick={() => {
                                close();
                                onCreateNewRole({ emailAddress, selectedRoleIds });
                              }}
                            >
                              {t('newRole', { defaultValue: 'New Role' })}
                            </Button>
                          )
                        : undefined
                    }
                  />
                }
              />
            )}
          </Box>
        </Card>
      </div>
    </SideDrawer>
  );
};

export default CreateUserDrawer;
